import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drives an opt-in, drag-to-resize width for a right-side SlidePanel.
 *
 * Width is tracked in `vw` (viewport-relative, no hardcoded px) and clamped to
 * [minVw, maxVw]. Dragging the panel's LEFT edge to the left widens it; the
 * minimum keeps the panel wide enough for tables/code, and the maximum leaves a
 * sliver of page context visible behind it. On narrow viewports resizing is
 * disabled and the panel falls back to full width (handled in CSS).
 */
interface Options {
  /** Whether resizing is requested by the caller at all. */
  resizable: boolean;
  defaultVw: number;
  /** Optional controlled width, for a session-owned panel. */
  widthVw?: number;
  onWidthChange?: (widthVw: number) => void;
  minVw: number;
  maxVw: number;
  /** Below this viewport width (px) resizing is disabled → full width. */
  narrowBreakpointPx?: number;
}

interface Result {
  /** Current width in vw. */
  widthVw: number;
  /** True when the drag handle should be interactive (resizable + wide enough). */
  resizeEnabled: boolean;
  /** mousedown handler for the drag handle. */
  startResize: (e: React.MouseEvent) => void;
  /** True while a drag is in progress (for styling). */
  isResizing: boolean;
  resizeTo: (widthVw: number) => void;
}

export function useResizableWidth({
  resizable,
  defaultVw,
  widthVw: controlledWidth,
  onWidthChange,
  minVw,
  maxVw,
  narrowBreakpointPx = 768,
}: Options): Result {
  const [localWidth, setLocalWidth] = useState(defaultVw);
  const widthVw = controlledWidth ?? localWidth;
  const isControlled = controlledWidth !== undefined;
  const resizeTo = useCallback(
    (value: number) => {
      const next = Math.min(maxVw, Math.max(minVw, value));
      if (!isControlled) setLocalWidth(next);
      onWidthChange?.(next);
    },
    [isControlled, minVw, maxVw, onWidthChange],
  );
  const [isResizing, setIsResizing] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const dragStartX = useRef(0);
  const dragStartVw = useRef(defaultVw);
  const dragViewportWidth = useRef(1);

  // Track narrow viewports so resizing is only offered where it's practical.
  useEffect(() => {
    if (!resizable || typeof window === "undefined") return;
    const mql = window.matchMedia(`(max-width: ${narrowBreakpointPx}px)`);
    const sync = () => setIsNarrow(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, [resizable, narrowBreakpointPx]);

  // Attach the drag listeners only while a resize is in progress; the cleanup
  // detaches them and restores the cursor even if the panel unmounts mid-drag.
  useEffect(() => {
    if (!isResizing) return;
    if (!resizable || isNarrow) {
      setIsResizing(false);
      return;
    }

    // The viewport cannot change mid-drag, so read it once: reading
    // `innerWidth` per move would force layout against the width this drag
    // just wrote.
    const viewportWidth = dragViewportWidth.current;
    let frame = 0;
    let pendingX = 0;
    const commit = () => {
      frame = 0;
      // Dragging the left edge leftwards (smaller clientX) widens the panel.
      const deltaVw = ((dragStartX.current - pendingX) / viewportWidth) * 100;
      resizeTo(dragStartVw.current + deltaVw);
    };
    // Coalesce to one width update per frame; mousemove fires far faster than
    // the panel can paint, and each update re-renders the panel's subtree.
    const onMove = (e: MouseEvent) => {
      pendingX = e.clientX;
      frame ||= requestAnimationFrame(commit);
    };
    const onUp = () => setIsResizing(false);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    window.addEventListener("blur", onUp);
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    // Prevent text selection / flickering cursors while dragging.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";

    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isResizing, resizeTo, resizable, isNarrow]);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      if (!resizable || isNarrow || e.button !== 0) return;
      e.preventDefault();
      dragStartX.current = e.clientX;
      dragStartVw.current = widthVw;
      dragViewportWidth.current = window.innerWidth || 1;
      setIsResizing(true);
    },
    [resizable, isNarrow, widthVw],
  );

  return {
    widthVw,
    resizeEnabled: resizable && !isNarrow,
    startResize,
    isResizing,
    resizeTo,
  };
}

export default useResizableWidth;
